import { pick, uniq, without } from "lodash";
import { Coordinates } from "sigma/types";
import { FC, useEffect, useState } from "react";
import { useSigma, useRegisterEvents } from "react-sigma-v2";
import { WymmoDirectory } from "../types";

export const DraggingController: FC<{
  dataset: WymmoDirectory;
  disabled?: boolean;
}> = ({ dataset, disabled }) => {
  const sigma = useSigma();
  const registerEvents = useRegisterEvents();
  const [dragState, setDragState] = useState<
    | { type: "idle" }
    | {
        type: "dragging";
        hasDragged: boolean;
        initialMousePosition: Coordinates;
        initialNodesPosition: Record<string, Coordinates>;
      }
  >({ type: "idle" });

  let tag_keys = dataset.tags.map((tag) => tag.key);
  const [fixedNodes, setFixedNodes] = useState<string[]>(tag_keys);

  useEffect(() => {
    registerEvents({
      clickNode: ({ node }) => {
        if (fixedNodes.includes(node)) setFixedNodes(without(fixedNodes, node));
      },
      downNode: ({ node, event }) => {
        const graph = sigma.getGraph();
        const { x, y } = sigma.viewportToGraph(event);

        if (!disabled)
          setDragState({
            type: "dragging",
            hasDragged: false,
            initialMousePosition: { x, y },
            initialNodesPosition: {
              [node]: pick(graph.getNodeAttributes(node), ["x", "y"]),
            },
          });
      },
      mouseup: () => {
        if (dragState.type === "dragging") {
          setDragState({ type: "idle" });
          setFixedNodes(
            uniq(fixedNodes.concat(Object.keys(dragState.initialNodesPosition)))
          );
        }
      },
      mousemove: (e) => {
        if (dragState.type === "dragging") {
          const graph = sigma.getGraph();

          // Set new positions for nodes:
          const newPosition = sigma.viewportToGraph(e);
          const delta = {
            x: newPosition.x - dragState.initialMousePosition.x,
            y: newPosition.y - dragState.initialMousePosition.y,
          };

          for (const node in dragState.initialNodesPosition) {
            const initialPosition = dragState.initialNodesPosition[node];
            graph.setNodeAttribute(node, "x", initialPosition.x + delta.x);
            graph.setNodeAttribute(node, "y", initialPosition.y + delta.y);
          }

          // Prevent sigma to move camera:
          e.preventSigmaDefault();
          e.original.preventDefault();
        }
      },
    });
  }, [registerEvents, sigma, dragState, disabled]);

  useEffect(() => {
    const graph = sigma.getGraph();
    const fixedNodesSet = new Set(fixedNodes);

    graph.forEachNode((node) => {
      const isFixed =
        fixedNodesSet.has(node) ||
        (dragState.type === "dragging" && dragState.initialNodesPosition[node]);

      graph.setNodeAttribute(node, "fixed", isFixed);
      graph.setNodeAttribute(node, "highlighted", isFixed);
    });
  }, [dragState, fixedNodes]);

  return null;
};

export default DraggingController;
